import React, { useMemo } from "react";

const CHART_HEIGHT = 200;
const PADDING = { top: 8, right: 8, bottom: 0, left: 0 };
const CASH_IN_COLOR = "#059669";
const CASH_OUT_COLOR = "#dc2626";
const CASH_IN_FILL_OPACITY = 0.15;
const CASH_OUT_FILL_OPACITY = 0.1;

export interface CashInOutChartProps {
  yAxisLabels: string[];
  monthLabels: string[];
  /** Raw values for multi-series area chart (same scale) */
  cashIn: number[];
  cashOut: number[];
  tooltips?: { monthLabel: string; cashIn: string; cashOut: string }[];
  currentMonthLabel?: string;
}

function smoothPathToBottom(
  points: { x: number; y: number }[],
  bottomY: number,
): string {
  if (points.length === 0) return "";
  if (points.length === 1) {
    return `M ${points[0].x} ${points[0].y} L ${points[0].x} ${bottomY} Z`;
  }
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cpx = (prev.x + curr.x) / 2;
    d += ` Q ${cpx} ${prev.y} ${curr.x} ${curr.y}`;
  }
  d += ` L ${points[points.length - 1].x} ${bottomY} L ${points[0].x} ${bottomY} Z`;
  return d;
}

function linePath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return "";
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cpx = (prev.x + curr.x) / 2;
    d += ` Q ${cpx} ${prev.y} ${curr.x} ${curr.y}`;
  }
  return d;
}

export function CashInOutChart({
  yAxisLabels,
  monthLabels,
  cashIn,
  cashOut,
  tooltips,
  currentMonthLabel,
}: CashInOutChartProps) {
  const gradientInId = React.useId().replace(/:/g, "-") + "in";
  const gradientOutId = React.useId().replace(/:/g, "-") + "out";
  const chartWidth = 800;
  const innerWidth = chartWidth - PADDING.left - PADDING.right;
  const innerHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  const bottomY = PADDING.top + innerHeight;

  const { areaInPath, areaOutPath, strokeInPath, strokeOutPath, hoverRects } =
    useMemo(() => {
      const n = Math.max(cashIn.length, cashOut.length);
      if (n === 0)
        return {
          areaInPath: "",
          areaOutPath: "",
          strokeInPath: "",
          strokeOutPath: "",
          hoverRects: [] as { x: number; width: number; tip: string }[],
        };

      const maxVal = Math.max(...cashIn, ...cashOut, 1);

      const toPoints = (values: number[]) => {
        const pts: { x: number; y: number }[] = [];
        for (let i = 0; i < values.length; i++) {
          const x = PADDING.left + (i / Math.max(n - 1, 1)) * innerWidth;
          const y =
            PADDING.top + innerHeight - (values[i] / maxVal) * innerHeight;
          pts.push({ x, y: Math.max(PADDING.top, y) });
        }
        return pts;
      };

      const pointsIn = toPoints(cashIn);
      const pointsOut = toPoints(cashOut);

      const rects: { x: number; width: number; tip: string }[] = [];
      const step = innerWidth / Math.max(n - 1, 1);
      for (let i = 0; i < n; i++) {
        const x = PADDING.left + i * step - step / 2;
        const tip = tooltips?.[i];
        rects.push({
          x: Math.max(0, x),
          width: step,
          tip: tip
            ? `${tip.monthLabel}: In ${tip.cashIn}, Out ${tip.cashOut}`
            : "",
        });
      }

      return {
        areaInPath: smoothPathToBottom(pointsIn, bottomY),
        areaOutPath: smoothPathToBottom(pointsOut, bottomY),
        strokeInPath: linePath(pointsIn),
        strokeOutPath: linePath(pointsOut),
        hoverRects: rects,
      };
    }, [cashIn, cashOut, tooltips, innerWidth, innerHeight, bottomY]);

  return (
    <div className="flex min-w-0 w-full flex-col items-start overflow-hidden">
      <div className="flex w-full min-w-0 items-end gap-3">
        <div className="flex flex-col items-end gap-6 pb-8 flex-shrink-0">
          {yAxisLabels.map((l, i) => (
            <span
              key={i}
              className="text-caption font-caption text-neutral-400"
            >
              {l}
            </span>
          ))}
        </div>
        <div className="flex min-w-0 flex-1 flex-col items-start overflow-hidden">
          <div
            className="relative w-full overflow-hidden"
            style={{ height: CHART_HEIGHT }}
          >
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute left-0 right-0 top-0 h-px bg-neutral-100" />
              <div className="absolute left-0 right-0 top-1/4 h-px bg-neutral-100" />
              <div className="absolute left-0 right-0 top-1/2 h-px bg-neutral-100" />
              <div className="absolute left-0 right-0 top-3/4 h-px bg-neutral-100" />
              <div className="absolute left-0 right-0 bottom-0 h-px bg-neutral-200" />
            </div>
            <svg
              viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT}`}
              preserveAspectRatio="none"
              className="absolute inset-0 h-full w-full"
              aria-hidden
            >
              <defs>
                <linearGradient
                  id={gradientOutId}
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop
                    offset="0%"
                    stopColor={CASH_OUT_COLOR}
                    stopOpacity={CASH_OUT_FILL_OPACITY}
                  />
                  <stop
                    offset="100%"
                    stopColor={CASH_OUT_COLOR}
                    stopOpacity={0.02}
                  />
                </linearGradient>
                <linearGradient
                  id={gradientInId}
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop
                    offset="0%"
                    stopColor={CASH_IN_COLOR}
                    stopOpacity={CASH_IN_FILL_OPACITY}
                  />
                  <stop
                    offset="100%"
                    stopColor={CASH_IN_COLOR}
                    stopOpacity={0.02}
                  />
                </linearGradient>
              </defs>
              <path d={areaOutPath} fill={`url(#${gradientOutId})`} />
              <path d={areaInPath} fill={`url(#${gradientInId})`} />
              <path
                d={strokeOutPath}
                fill="none"
                stroke={CASH_OUT_COLOR}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d={strokeInPath}
                fill="none"
                stroke={CASH_IN_COLOR}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {hoverRects.map((r, i) => (
                <g key={i}>
                  {r.tip && <title>{r.tip}</title>}
                  <rect
                    x={r.x}
                    y={0}
                    width={r.width}
                    height={CHART_HEIGHT}
                    fill="transparent"
                    className="cursor-default"
                    style={{ pointerEvents: "auto" }}
                    aria-label={r.tip || undefined}
                  />
                </g>
              ))}
            </svg>
            {currentMonthLabel && (
              <div className="flex items-center gap-2 absolute bottom-2 right-2 pointer-events-none">
                <div className="flex h-2 w-2 flex-none rounded-full bg-[#60a5fa]" />
                <span className="text-caption font-caption text-neutral-500">
                  {currentMonthLabel}
                </span>
              </div>
            )}
          </div>
          <div className="flex w-full items-center justify-between gap-0 pt-3">
            {monthLabels.map((l, i) => (
              <span
                key={i}
                className="flex-1 min-w-0 text-center text-caption font-caption text-neutral-500 truncate"
                title={l}
              >
                {l}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
